import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { buildTuftPlan, TuftPlan } from "../lib/tuft";
import { renderTuftScene } from "../lib/tuftRender";
import { scrollToId } from "../lib/scroll";

const STAGES = [
  "Transferring the design outline",
  "Punching the ivory field",
  "Tufting the forest border & petals",
  "Sculpting burgundy & brass details",
  "Carving and fluffing the pile",
  "Your rug is ready",
];

function stageFor(p: number) {
  if (p < 0.08) return 0;
  if (p < 0.35) return 1;
  if (p < 0.62) return 2;
  if (p < 0.86) return 3;
  if (p < 0.93) return 4;
  return 5;
}

function useTuftCanvas(reduced: boolean, staticProgress: boolean) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const planRef = useRef<TuftPlan | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const progRef = useRef(0);
  const [ui, setUi] = useState({ pct: 0, stage: 0, overlay: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.parentElement) return;
    if (!planRef.current) planRef.current = buildTuftPlan();
    const plan = planRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const quality = window.innerWidth < 640 ? 0.5 : 1;
    let cssW = 0;
    let cssH = 0;
    let visible = true;
    let dirty = true;
    let raf = 0;

    const draw = (p: number) => {
      if (cssW > 0 && cssH > 0) renderTuftScene(ctx, cssW, cssH, plan, p, dpr, quality);
    };
    const resize = () => {
      const r = canvas.parentElement!.getBoundingClientRect();
      cssW = r.width;
      cssH = r.height;
      canvas.width = Math.max(1, Math.round(cssW * dpr));
      canvas.height = Math.max(1, Math.round(cssH * dpr));
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      dirty = true;
    };

    const ro = new ResizeObserver(() => {
      resize();
      if (reduced) draw(1);
    });
    ro.observe(canvas.parentElement);
    resize();

    if (reduced) {
      draw(1);
      return () => ro.disconnect();
    }

    const io = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
    });
    io.observe(canvas);

    const loop = () => {
      if (dirty && visible) {
        draw(progRef.current);
        dirty = false;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const total = Math.max(1, rect.height - vh);
      const p = Math.max(0, Math.min(1, -rect.top / total));
      if (Math.abs(p - progRef.current) > 0.0004) {
        progRef.current = p;
        dirty = true;
      }
      const bp = Math.max(0, Math.min(1, (p - 0.08) / 0.84));
      const pct = Math.round(bp * 100);
      const stage = stageFor(p);
      const overlay = Math.max(0, Math.min(1, (p - 0.925) / 0.055));
      setUi((prev) =>
        prev.pct === pct && prev.stage === stage && Math.abs(prev.overlay - overlay) < 0.02
          ? prev
          : { pct, stage, overlay }
      );
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [reduced, staticProgress]);

  return { canvasRef, sectionRef, ui };
}

function Eyebrow() {
  return (
    <div className="text-center px-5">
      <p className="flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-[0.22em] text-burgundy font-medium">
        <span className="inline-block h-2 w-2 rounded-full bg-brass" aria-hidden="true" />
        Watch a rug being tufted
      </p>
      <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-forest">
        From sketch to soft.
      </h2>
    </div>
  );
}

export function TuftingSection() {
  const reduced = useReducedMotion() ?? false;
  const { canvasRef, sectionRef, ui } = useTuftCanvas(reduced, false);

  if (reduced) {
    return (
      <section id="tufting-experience" data-testid="tufting-experience" className="py-20 sm:py-24">
        <div className="max-w-3xl mx-auto px-5">
          <Eyebrow />
          <div className="relative mt-8 rounded-3xl overflow-hidden border border-brass/40 bg-forest/5 aspect-[10/7]">
            <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" data-testid="tufting-canvas" />
          </div>
          <div className="mt-8 text-center">
            <p className="font-serif text-2xl sm:text-3xl text-forest">
              From a little idea to something you can feel.
            </p>
            <button
              onClick={() => scrollToId("customise")}
              data-testid="tufting-create-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-forest text-ivory font-medium px-7 py-3.5 transition-all duration-200 hover:bg-forest-deep hover:-translate-y-0.5 shadow-tuft"
            >
              Create My Custom Rug
              <ArrowDown className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="tufting-experience"
      data-testid="tufting-experience"
      className="relative"
      style={{ height: "320vh" }}
    >
      <div className="sticky top-0 h-screen flex flex-col overflow-hidden texture-weave">
        <div className="pt-8 sm:pt-10">
          <Eyebrow />
        </div>

        <div className="relative flex-1 min-h-0 mt-2">
          <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" data-testid="tufting-canvas" />

          <div
            className="absolute inset-x-0 bottom-2 flex justify-center px-4"
            style={{ opacity: ui.overlay, pointerEvents: ui.overlay > 0.5 ? "auto" : "none" }}
          >
            <div className="bg-ivory/90 backdrop-blur-sm border border-brass/40 rounded-3xl px-6 sm:px-10 py-5 sm:py-7 text-center shadow-tuft-lg max-w-xl">
              <p className="font-serif text-xl sm:text-3xl text-forest leading-snug">
                From a little idea to something you can feel.
              </p>
              <button
                onClick={() => scrollToId("customise")}
                data-testid="tufting-create-button"
                className="mt-4 sm:mt-5 inline-flex items-center gap-2 rounded-full bg-forest text-ivory font-medium px-6 py-3 text-sm sm:text-base transition-all duration-200 hover:bg-forest-deep hover:-translate-y-0.5 shadow-tuft"
              >
                Create My Custom Rug
                <ArrowDown className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="pb-6 px-5">
          <div className="max-w-2xl mx-auto flex items-center gap-3" data-testid="tufting-progress-bar">
            <span
              className="text-xs sm:text-sm text-charcoal/70 whitespace-nowrap"
              data-testid="tufting-progress-label"
            >
              {STAGES[ui.stage]}
            </span>
            <span className="flex-1 h-1.5 rounded-full bg-forest/10 overflow-hidden">
              <span
                className="block h-full bg-forest rounded-full transition-[width] duration-200 ease-out"
                style={{ width: `${ui.pct}%` }}
              />
            </span>
            <span className="text-xs sm:text-sm font-medium text-forest tabular-nums whitespace-nowrap">
              {ui.pct}%
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
